<template>
    <common-dropdown :hasArrow="false" arrowPositionX="top" arrowPositionY="right" class="context-menu">
        <common-content-wrapper width="medium">
            <common-content-spacer
                v-for="(item, index) of displayItems"
                :key="index"
                height="tiny"
                :class="[item.class ? item.class : '', { 'disabled-item': item.disabled }]"
                @click="!item.disabled && executeItem(item)"
            >
                {{ item.label }}
            </common-content-spacer>
        </common-content-wrapper>
    </common-dropdown>
</template>

<script>
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import UsersAPI from 'api/UsersAPI'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import cloneDeep from 'lodash.clonedeep'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        mixins: [ReferralStatusMixin, ReferralConfirmMixin],

        props: {
            optionProp: { type: Object, required: true },
            sticky: { type: Boolean, default: true },
            asContextMenu: { type: Boolean, default: false },
            showCancelled: { type: Boolean, default: false },
        },

        data() {
            return {
                option: {},
            }
        },

        computed: {
            isUnavailable() {
                return !!this.option.value.isUnavailable
            },

            referral() {
                return this.option.value.referral
            },

            referralId() {
                return (this.referral || {}).id || 0
            },

            isReferringPhysician() {
                return this.$route.params.type === 'made'
            },

            allItems() {
                return [
                    {
                        isValid:
                            !this.asContextMenu && (this.isReferralOk(this.referral) || this.isReferralPending(this.referral)),
                        label: this.referralStatusDescription,
                        name: 'status',
                        class: 'plain',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            !!this.referral &&
                            !(this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled),
                        label: this.$t(`pages.referrals.new.dropdown.label.view_referral`),
                        name: 'view',
                        route: this.getRoute(),
                    },
                    {
                        isValid: this.asContextMenu && this.shouldShowReferralDetails(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.download_pdf`),
                        name: 'downloadPdf',
                    },
                    {
                        isValid: this.asContextMenu && this.isReferralReschedulable(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.follow_up_visit`),
                        name: 'follow-up-visit',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.isReferralPending(this.referral) &&
                            !this.isAppointmentInPast(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.confirm'),
                        name: 'confirm',
                        route: this.getRoute('confirm'),
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            (this.isReferralPending(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && !this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.decline'),
                        name: 'decline',
                        route: this.getRoute('decline'),
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                this.isReferralPending(this.referral) ||
                                this.isReferralToBeRescheduled(this.referral) ||
                                this.isReferralPendingReschedule(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.reschedule'),
                        name: 'reschedule',
                        route: this.getRoute('reschedule'),
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.cancel'),
                        name: 'cancel',
                        route: this.getRoute('cancel'),
                    },
                    {
                        isValid: !this.isReferringPhysician && this.isReferralWaitingForReport(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.add_report'),
                        name: 'addReport',
                        route: { name: `referrals.received.add-report`, params: { id: this.referralId } },
                    },
                    {
                        isValid: this.isReferringPhysician && this.isReferralWaitingForReport(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.request_report'),
                        name: 'requestReport',
                    },
                    {
                        isValid:
                            this.isReferringPhysician &&
                            !this.isReferralWaitingForReport(this.referral) &&
                            !this.isReferralCancelledOrNoShow(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.cancel'),
                        name: 'cancel',
                    },
                    {
                        isValid:
                            this.asContextMenu &&
                            !this.isReferringPhysician &&
                            this.isAppointmentInPast(this.referral) &&
                            this.isReferralWaitingForPatient(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.patient_seen'),
                        name: 'patientSeen',
                        route: this.getRoute(),
                    },
                    {
                        isValid: this.isReferringPhysician && this.isReferralError(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.match'),
                        name: 'match',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)) &&
                            !this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_unavailable`),
                        fn: this.setUnavailable,
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (this.$auth.isPracticePremium() || this.$auth.isPracticeTrial()) &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)),
                        label: this.$t(`pages.referrals.new.dropdown.label.create_referral`),
                        fn: this.createReferral,
                        disabled: this.isUnavailable,
                    },
                    {
                        isValid: !this.asContextMenu && !this.referral && this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_available`),
                        fn: this.setAvailable,
                    },
                ]
            },

            displayItems() {
                return this.allItems.filter((item) => item.isValid)
            },

            referralStatusDescription() {
                if (!this.referral) return ''
                return this.$t(`pages.referrals.tracking.status_description.${this.referral.status}`)
            },

            slotData() {
                return {
                    userId: this.$auth.getActiveMember().id,
                    date: this.option.value.date,
                    startTime: this.option.value.startTime,
                    endTime: this.option.value.endTime,
                    practiceId: this.$auth.getPractice().id,
                    practiceLocationId: Number(this.$route.query.locationId) || 0,
                }
            },
        },

        methods: {
            getRoute(action, actionAs = 'query') {
                let route = { name: `referrals.received.show`, params: { id: this.referralId } }
                if (action) {
                    route[actionAs] = { action: action }
                }
                return route
            },

            executeItem(item) {
                if (this.asContextMenu) {
                    if (item.name) {
                        this.emitEvent({ name: item.name, payload: { isReferringPhysician: this.isReferringPhysician } })
                        this.emitEvent({ name: 'closeReferralContextMenu', payload: {} })
                    }
                } else if (item.fn) {
                    let that = this
                    Promise.resolve()
                        .then(() => item.fn())
                        .then(() => {
                            that.emitEvent({ name: 'closeReferralContextMenu' })
                        })
                } else if (item.route) {
                    this.$router.push(item.route)
                }
            },

            setUnavailable() {
                return UsersAPI.setAvailabilitySlotUnavailable(this.slotData).then(() => {
                    this.option.value.isUnavailable = true
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },

            createReferral() {
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                    const query = {
                        userId: this.slotData.userId,
                        practiceType: this.$auth.getPractice().practiceType,
                        searchBy: 'zipcode',
                        insurancePlanId: null,
                        specialityId: this.$auth.getActiveMember().specialities[0].speciality.id,
                        subSpecialityId: this.$auth.getActiveMember().specialities[0].subSpeciality.id,
                        physicianId: this.slotData.userId,
                        practiceId: this.slotData.practiceId,
                        practiceLocationId: this.slotData.practiceLocationId,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }

                    this.$router.push({
                        name: `referrals.new.make`,
                        query: query,
                    })
                } else {
                    const query = {
                        practiceLocationId: this.slotData.practiceLocationId,
                        practiceType: this.$auth.getPractice().practiceType,
                        practiceId: this.slotData.practiceId,
                        practiceName: this.$auth.getPractice().name,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }
                    this.$router.push({
                        name: `referrals.services.tests`,
                        query: query,
                    })
                }
            },

            setAvailable() {
                return UsersAPI.setAvailabilitySlotAvailable(this.slotData).then(() => {
                    this.option.value.isUnavailable = false
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },
        },

        beforeMount() {
            this.option = cloneDeep(this.optionProp)
        },

        mounted() {
            if (this.sticky) {
                const dropdown = this.$el
                const parent = dropdown.parentNode

                const setTop = (top) => (dropdown.style.top = top + 'px')
                const setLeft = (left) => (dropdown.style.left = left + 'px')

                //calculating top
                const pageHeight = window.innerHeight
                const dropdownAbsTop = dropdown.getBoundingClientRect().top
                const parentAbsTop = parent.getBoundingClientRect().top
                const topUnder = dropdown.offsetTop - (dropdownAbsTop - parentAbsTop) + parent.offsetHeight //directly below
                const topOver = dropdown.offsetTop - (dropdownAbsTop - parentAbsTop) - dropdown.offsetHeight //directly above
                setTop(topUnder)
                if (dropdown.getBoundingClientRect().top + dropdown.offsetHeight > pageHeight) {
                    setTop(topOver)
                }
                //calculating top end

                //calculating left
                const equalRight = parent.offsetLeft - (dropdown.offsetWidth - parent.offsetWidth) //equal right edges
                setLeft(equalRight)
            }
        },
    }
</script>
<style lang="css">
    .disabled-item {
        opacity: 0.5;
        pointer-events: none;
        cursor: not-allowed;
    }
</style>
